import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Store, Search, RefreshCw, Send, Ban, ShieldCheck, Loader2, MailCheck, MailWarning,
  AlertTriangle, FileDown, CheckCircle2, Clock, XCircle, HelpCircle, Filter, ListX,
  CalendarRange, UserRound, Inbox,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const BRAND = "#EE722E";

function frenchDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// Métadonnées d'affichage des 4 statuts de suivi manuel.
const STATUS_META = {
  EN_ATTENTE: { label: "En attente", cls: "bg-[rgba(233,184,0,0.16)] text-[#5A4200]", Icon: Clock },
  CONFIRME: { label: "Confirmé", cls: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]", Icon: CheckCircle2 },
  REFUSE: { label: "Refusé", cls: "bg-[rgba(192,57,43,0.14)] text-[#8F2A20]", Icon: XCircle },
  SANS_REPONSE: { label: "Sans réponse", cls: "bg-[rgba(24,24,24,0.08)] text-[rgba(24,24,24,0.65)]", Icon: HelpCircle },
};
const STATUS_ORDER = ["EN_ATTENTE", "CONFIRME", "REFUSE", "SANS_REPONSE"];

// Déclenche le téléchargement d'un export authentifié (blob).
async function downloadAuth(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
    toast.success("Export téléchargé");
  } catch {
    toast.error("Export impossible");
  }
}

// ---------------------------------------------------------------------------
// Ligne de tableau
// ---------------------------------------------------------------------------
function ConfirmationRow({ row, onResend, onBlock, onStatus }) {
  const meta = STATUS_META[row.statut] || STATUS_META.EN_ATTENTE;
  const refuse = row.statut === "REFUSE";
  const alreadyResent = (row.renvoi_manuel_count || 0) >= 1;

  return (
    <tr className="border-b border-[var(--border-soft)] align-top hover:bg-[var(--surface-2)]/50"
      data-testid={`conf-row-${row.ack_id || row.email_log_id}`}>
      {/* Magasin + client final */}
      <td className="px-3 py-3">
        <div className="flex items-center gap-1.5">
          <Store className="h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
          <span className="font-semibold text-ink" data-testid={`conf-magasin-${row.ack_id}`}>
            {row.magasin || "—"}
          </span>
          {row.blocklisted && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(192,57,43,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#8F2A20]"
              data-testid={`conf-blocked-tag-${row.ack_id}`}>
              <Ban className="h-3 w-3" /> Ne plus envoyer
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-muted-ink">{[row.ville, row.client_final].filter(Boolean).join(" · ")}</div>
        {row.adresse && <div className="mt-0.5 text-[11px] text-muted-ink">{row.adresse}</div>}
      </td>

      {/* Responsable + email */}
      <td className="px-3 py-3">
        <div className="flex items-center gap-1.5 text-ink">
          <UserRound className="h-3.5 w-3.5 shrink-0 text-muted-ink" />
          <span className="font-medium">{row.responsable_nom || "Responsable"}</span>
        </div>
        <div className="mt-0.5 break-all text-xs text-muted-ink" data-testid={`conf-email-${row.ack_id}`}>
          {row.responsable_email || "—"}
        </div>
      </td>

      {/* Dates d'intervention */}
      <td className="px-3 py-3">
        <span className="inline-flex items-center gap-1 text-xs text-ink">
          <CalendarRange className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          {row.dates_intervention || "—"}
        </span>
      </td>

      {/* Envoi + clic reçu */}
      <td className="px-3 py-3 text-xs">
        <div className="text-ink">{frenchDateTime(row.date_envoi)}</div>
        <div className="mt-1">
          {row.clic_recu ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#0F5A2F]"
              data-testid={`conf-clic-${row.ack_id}`} title={frenchDateTime(row.clic_recu_le)}>
              <MailCheck className="h-3 w-3" /> Reçu
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(24,24,24,0.06)] px-1.5 py-0.5 text-[10px] font-bold text-muted-ink"
              data-testid={`conf-noclic-${row.ack_id}`}>
              <MailWarning className="h-3 w-3" /> Pas de clic
            </span>
          )}
        </div>
      </td>

      {/* Statut modifiable */}
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
            <meta.Icon className="h-3.5 w-3.5" /> {meta.label}
          </span>
        </div>
        <div className="mt-1.5">
          <Select value={row.statut} onValueChange={(v) => onStatus(row, v)}>
            <SelectTrigger className="h-8 w-[150px] bg-white text-xs" data-testid={`conf-status-select-${row.ack_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s} data-testid={`conf-status-opt-${s}`}>
                  {STATUS_META[s].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </td>

      {/* Actions (exactement 2) */}
      <td className="px-3 py-3">
        <div className="flex flex-col gap-1.5">
          <Button size="sm" variant="outline" disabled={refuse || !row.ack_id}
            onClick={() => onResend(row)}
            className="h-8 justify-start border-[rgba(238,114,46,0.4)] bg-white text-[#8A3A10] hover:bg-[#FFF3E9] disabled:opacity-40"
            data-testid={`conf-resend-btn-${row.ack_id}`}
            title={refuse ? "Refusé : renvoi désactivé" : "Renvoyer la confirmation (renvoi manuel)"}>
            <Send className="mr-1.5 h-3.5 w-3.5" /> Renvoyer la confirmation
            {alreadyResent && !refuse && (
              <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-[rgba(233,184,0,0.2)] px-1.5 text-[10px] font-bold text-[#5A4200]">
                <AlertTriangle className="h-3 w-3" />{row.renvoi_manuel_count}
              </span>
            )}
          </Button>
          <Button size="sm" variant="outline" disabled={row.blocklisted || !row.responsable_email}
            onClick={() => onBlock(row)}
            className="h-8 justify-start border-[rgba(192,57,43,0.35)] bg-white text-[#8F2A20] hover:bg-[rgba(192,57,43,0.06)] disabled:opacity-40"
            data-testid={`conf-block-btn-${row.ack_id}`}
            title="Ne plus jamais envoyer de confirmation à cette adresse">
            <Ban className="mr-1.5 h-3.5 w-3.5" /> Ne plus envoyer de confirmation
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Page principale
// ---------------------------------------------------------------------------
export default function ConfirmationsMagasins() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("TOUS");
  const [clientF, setClientF] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busyId, setBusyId] = useState(null);

  // Dialogues contrôlés
  const [resendTarget, setResendTarget] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [blocklistOpen, setBlocklistOpen] = useState(false);
  const [blocklist, setBlocklist] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF && statusF !== "TOUS") params.status = statusF;
      if (clientF.trim()) params.client = clientF.trim();
      if (from) params.date_from = new Date(from).toISOString();
      if (to) params.date_to = new Date(to + "T23:59:59").toISOString();
      if (q.trim()) params.search = q.trim();
      const { data } = await api.get("/admin/confirmations", { params });
      setRows(Array.isArray(data.items) ? data.items : []);
    } catch {
      setRows([]);
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [statusF, clientF, from, to, q]);

  useEffect(() => { load(); }, [load]);

  const loadBlocklist = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/confirmations/blocklist");
      setBlocklist(Array.isArray(data.items) ? data.items : []);
    } catch { setBlocklist([]); }
  }, []);

  // --- Actions ---
  const changeStatus = async (row, value) => {
    if (value === row.statut) return;
    setBusyId(row.ack_id);
    try {
      await api.patch(`/admin/confirmations/${row.ack_id}/status`, { status: value });
      toast.success(`Statut mis à jour : ${STATUS_META[value]?.label || value}`);
      setRows((rs) => rs.map((r) => (r.ack_id === row.ack_id ? { ...r, statut: value } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mise à jour impossible");
    } finally { setBusyId(null); }
  };

  const doResend = async () => {
    const row = resendTarget;
    if (!row) return;
    setBusyId(row.ack_id);
    try {
      const { data } = await api.post(`/admin/confirmations/${row.ack_id}/resend`);
      toast.success(`Confirmation renvoyée (envoi ${data.status}) — renvoi n°${data.renvoi_manuel_count}`);
      setResendTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renvoi impossible");
    } finally { setBusyId(null); }
  };

  const doBlock = async () => {
    const row = blockTarget;
    if (!row) return;
    setBusyId(row.ack_id);
    try {
      await api.post("/admin/confirmations/blocklist", {
        email: row.responsable_email, reason: blockReason.trim() || undefined, ack_id: row.ack_id });
      toast.success("Adresse ajoutée : plus aucune confirmation ne lui sera envoyée");
      setBlockTarget(null); setBlockReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ajout impossible");
    } finally { setBusyId(null); }
  };

  const removeFromBlocklist = async (email) => {
    try {
      await api.delete(`/admin/confirmations/blocklist/${encodeURIComponent(email)}`);
      toast.success("Adresse retirée de la liste");
      loadBlocklist(); load();
    } catch { toast.error("Retrait impossible"); }
  };

  const counts = useMemo(() => {
    const c = { TOUS: rows.length };
    STATUS_ORDER.forEach((s) => { c[s] = rows.filter((r) => r.statut === s).length; });
    return c;
  }, [rows]);

  const resetFilters = () => { setQ(""); setStatusF("TOUS"); setClientF(""); setFrom(""); setTo(""); };

  return (
    <>
      <PageHeader
        title="Confirmations magasins"
        subtitle="Pilotage manuel des confirmations envoyées aux responsables. Aucun rappel automatique — tout second envoi est manuel."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => { loadBlocklist(); setBlocklistOpen(true); }}
              className="border-[var(--border-soft)] bg-white" data-testid="conf-open-blocklist">
              <Ban className="mr-1.5 h-4 w-4" /> Blocklist
            </Button>
            <Button variant="outline" onClick={load} disabled={loading}
              className="border-[var(--border-soft)] bg-white" data-testid="conf-refresh">
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
            </Button>
          </div>
        }
      >
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (magasin, email…)" className="bg-white pl-9" data-testid="conf-search" />
        </div>
      </PageHeader>

      <PageBody>
        {/* Bandeau d'information sécurité */}
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-[rgba(31,157,85,0.25)] bg-[rgba(31,157,85,0.06)] px-3 py-2.5 text-[12px] text-[#0F5A2F]"
          data-testid="conf-safety-banner">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Le rappel de confirmation automatique est <b>désactivé (plafond 0)</b>. Un second envoi
            n'est possible que <b>manuellement</b> depuis cet écran, et jamais vers une adresse de la blocklist.
          </span>
        </div>

        {/* Exports */}
        <div className="mb-4 rounded-2xl border border-[var(--border-soft)] bg-white p-3.5 shadow-[var(--shadow-sm)]" data-testid="conf-exports">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
            <FileDown className="h-3.5 w-3.5" /> Exports nominatifs
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="border-[var(--border-soft)] bg-white"
              onClick={() => downloadAuth("/admin/confirmations/exports/purges.csv", "rappels_confirmation_purges_detaille.csv")}
              data-testid="conf-export-purges-csv">
              <FileDown className="mr-1.5 h-3.5 w-3.5" /> Rappels purgés — CSV
            </Button>
            <Button size="sm" variant="outline" className="border-[var(--border-soft)] bg-white"
              onClick={() => downloadAuth("/admin/confirmations/exports/purges.xlsx", "rappels_confirmation_purges_detaille.xlsx")}
              data-testid="conf-export-purges-xlsx">
              <FileDown className="mr-1.5 h-3.5 w-3.5" /> Rappels purgés — Excel
            </Button>
            <Button size="sm" variant="outline" className="border-[var(--border-soft)] bg-white"
              onClick={() => downloadAuth("/admin/confirmations/exports/partis.csv?days=60", "rappels_confirmation_partis_60j.csv")}
              data-testid="conf-export-partis-csv">
              <FileDown className="mr-1.5 h-3.5 w-3.5" /> Partis (60 j) — CSV
            </Button>
            <Button size="sm" variant="outline" className="border-[var(--border-soft)] bg-white"
              onClick={() => downloadAuth("/admin/confirmations/exports/partis.xlsx?days=60", "rappels_confirmation_partis_60j.xlsx")}
              data-testid="conf-export-partis-xlsx">
              <FileDown className="mr-1.5 h-3.5 w-3.5" /> Partis (60 j) — Excel
            </Button>
          </div>
        </div>

        {/* Filtres */}
        <div className="mb-4 flex flex-wrap items-end gap-2" data-testid="conf-filters">
          <div>
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Statut</label>
            <Select value={statusF} onValueChange={setStatusF}>
              <SelectTrigger className="h-9 w-[170px] bg-white text-sm" data-testid="conf-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TOUS">Tous les statuts ({counts.TOUS})</SelectItem>
                {STATUS_ORDER.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label} ({counts[s] || 0})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Client (association)</label>
            <Input value={clientF} onChange={(e) => setClientF(e.target.value)}
              placeholder="ex : AIF" className="h-9 w-[160px] bg-white text-sm" data-testid="conf-filter-client" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Du</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="h-9 w-[150px] bg-white text-sm" data-testid="conf-filter-from" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Au</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="h-9 w-[150px] bg-white text-sm" data-testid="conf-filter-to" />
          </div>
          <Button variant="outline" onClick={resetFilters}
            className="h-9 border-[var(--border-soft)] bg-white" data-testid="conf-filter-reset">
            <ListX className="mr-1.5 h-4 w-4" /> Réinitialiser
          </Button>
        </div>

        {/* Tableau */}
        {loading ? (
          <div className="space-y-2" data-testid="conf-loading">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="conf-empty">
            <Inbox className="h-10 w-10 text-muted-ink" />
            <h3 className="mt-3 text-base font-bold text-ink">Aucune confirmation</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-ink">Aucune confirmation ne correspond à ces filtres.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid="conf-table">
            <table className="w-full min-w-[980px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] text-left text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                  <th className="px-3 py-2.5">Magasin / Client final</th>
                  <th className="px-3 py-2.5">Responsable</th>
                  <th className="px-3 py-2.5">Dates d'intervention</th>
                  <th className="px-3 py-2.5">Envoyé le / Clic</th>
                  <th className="px-3 py-2.5">Statut</th>
                  <th className="px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <ConfirmationRow key={row.ack_id || row.email_log_id} row={row}
                    onResend={setResendTarget} onBlock={(r) => { setBlockTarget(r); setBlockReason(""); }}
                    onStatus={changeStatus} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>

      {/* Modal — Renvoyer la confirmation */}
      <AlertDialog open={!!resendTarget} onOpenChange={(o) => !o && setResendTarget(null)}>
        <AlertDialogContent data-testid="conf-resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Renvoyer la confirmation ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Un email de confirmation (<b>renvoi manuel</b>) va être envoyé à{" "}
                  <b>{resendTarget?.responsable_email}</b> pour le magasin{" "}
                  <b>{resendTarget?.magasin}</b>. Ce n'est jamais un rappel automatique.
                </p>
                {(resendTarget?.renvoi_manuel_count || 0) >= 1 && (
                  <div className="flex items-start gap-2 rounded-lg border border-[rgba(233,184,0,0.5)] bg-[#FFFDF5] px-3 py-2 text-[12px] font-medium text-[#5A4200]"
                    data-testid="conf-resend-warning">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      Attention : {resendTarget.renvoi_manuel_count} renvoi(s) manuel(s) ont déjà été
                      effectués pour ce magasin. Évitez de solliciter le responsable de façon répétée.
                    </span>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setResendTarget(null)}
              className="border-[var(--border-soft)] bg-white" data-testid="conf-resend-cancel">Annuler</Button>
            <Button onClick={doResend} disabled={busyId === resendTarget?.ack_id}
              className="text-white" style={{ background: BRAND }} data-testid="conf-resend-confirm">
              {busyId === resendTarget?.ack_id
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Send className="mr-1.5 h-4 w-4" /> Confirmer le renvoi</>}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal — Ne plus envoyer de confirmation */}
      <AlertDialog open={!!blockTarget} onOpenChange={(o) => !o && setBlockTarget(null)}>
        <AlertDialogContent data-testid="conf-block-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ne plus envoyer de confirmation ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  L'adresse <b>{blockTarget?.responsable_email}</b> sera ajoutée à la blocklist.
                  Plus aucune confirmation (initiale ou renvoi manuel) ne pourra lui être envoyée,
                  y compris depuis un nouveau spot. Le blocage est vérifié au moment de l'envoi.
                </p>
                <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="Motif (facultatif) — ex : refus par téléphone"
                  className="bg-white text-sm" data-testid="conf-block-reason" />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setBlockTarget(null)}
              className="border-[var(--border-soft)] bg-white" data-testid="conf-block-cancel">Annuler</Button>
            <Button onClick={doBlock} disabled={busyId === blockTarget?.ack_id}
              className="bg-[#B5311F] text-white hover:bg-[#9E2A1A]" data-testid="conf-block-confirm">
              {busyId === blockTarget?.ack_id
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Ban className="mr-1.5 h-4 w-4" /> Bloquer cette adresse</>}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog — Gestion de la blocklist */}
      <Dialog open={blocklistOpen} onOpenChange={setBlocklistOpen}>
        <DialogContent className="max-w-lg" data-testid="conf-blocklist-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ban className="h-4 w-4 text-[#B5311F]" /> Liste « Ne plus envoyer de confirmation »</DialogTitle>
            <DialogDescription>
              Adresses exclues de tout envoi de confirmation. Le blocage est appliqué côté serveur, au moment de l'envoi.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {blocklist.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[var(--border-soft)] py-8 text-center text-sm text-muted-ink" data-testid="conf-blocklist-empty">
                Aucune adresse bloquée.
              </div>
            ) : blocklist.map((b) => (
              <div key={b.email} className="flex items-center justify-between gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-3 py-2"
                data-testid={`conf-blocklist-item-${b.email}`}>
                <div className="min-w-0">
                  <div className="break-all text-sm font-medium text-ink">{b.email}</div>
                  <div className="text-[11px] text-muted-ink">
                    {[b.reason, b.created_by && `par ${b.created_by}`, frenchDateTime(b.created_at)].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => removeFromBlocklist(b.email)}
                  className="border-[var(--border-soft)] bg-white text-xs" data-testid={`conf-blocklist-remove-${b.email}`}>
                  Retirer
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
